import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

function Commissions() {
  const styles = useThemeStyles();
  const [commissions, setCommissions] = useState([]);
  const [totals, setTotals] = useState({
    totalEarned: 0,
    totalPending: 0,
    totalApproved: 0,
    totalPaid: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [commissionStatus, setCommissionStatus] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [debouncedReferralCode, setDebouncedReferralCode] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0
  });
  const [approveDialog, setApproveDialog] = useState(false);
  const [payDialog, setPayDialog] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const debounceTimerRef = useRef(null);

  // Debounce referral code input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedReferralCode(referralCode);
      setPage(1); // Reset to first page when filter changes
    }, 500); // Wait 500ms after user stops typing

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [referralCode]);

  useEffect(() => {
    fetchCommissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedMonth, selectedYear, commissionStatus, debouncedReferralCode, page]);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (selectedMonth) params.month = selectedMonth;
      if (selectedYear) params.year = selectedYear;
      if (commissionStatus) params.commissionStatus = commissionStatus;
      if (debouncedReferralCode) params.referralCode = debouncedReferralCode;
      
      const response = await api.get('/api/admin/referrals/commissions', { params });
      setCommissions(response.data.commissions || []);
      setTotals(response.data.totals || totals);
      setPagination(response.data.pagination || pagination);
    } catch (err) {
      setError('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await api.put(`/api/admin/referrals/commissions/${selectedCommission._id}/approve`);
      setSuccess('Commission approved successfully');
      setApproveDialog(false);
      setSelectedCommission(null);
      fetchCommissions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve commission');
    }
  };

  const handleMarkPaid = async () => {
    try {
      await api.put(`/api/admin/referrals/commissions/${selectedCommission._id}/mark-paid`);
      setSuccess('Commission marked as paid successfully');
      setPayDialog(false);
      setSelectedCommission(null);
      fetchCommissions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark commission as paid');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'paid': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const months = [
    { value: '', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= currentYear - 2; i--) {
    years.push(i);
  }

  if (loading && commissions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        Commissions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Earned
              </Typography>
              <Typography variant="h5">
                £{totals.totalEarned.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
              <Typography variant="h5" color="warning.main">
                £{totals.totalPending.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Approved
              </Typography>
              <Typography variant="h5" color="info.main">
                £{totals.totalApproved.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Paid
              </Typography>
              <Typography variant="h5" color="success.main">
                £{totals.totalPaid.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={selectedMonth}
            label="Month"
            onChange={(e) => {
              setSelectedMonth(e.target.value);
              setPage(1);
            }}
          >
            {months.map(month => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setPage(1);
            }}
          >
            {years.map(year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={commissionStatus}
            label="Status"
            onChange={(e) => {
              setCommissionStatus(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Referral Code"
          value={referralCode}
          onChange={(e) => {
            setReferralCode(e.target.value);
          }}
          sx={{ minWidth: 150 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Referrer</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Earned Date</TableCell>
              <TableCell>Paid Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {commissions.map((commission) => (
              <TableRow key={commission._id}>
                <TableCell>
                  <Chip label={commission.referralCode} size="small" />
                </TableCell>
                <TableCell>
                  {commission.userId ? (
                    `${commission.userId.firstName || ''} ${commission.userId.lastName || ''}`.trim() || commission.userId.email
                  ) : 'N/A'}
                </TableCell>
                <TableCell>
                  {commission.upgradePlan ? (
                    <Chip 
                      label={commission.upgradePlan.charAt(0).toUpperCase() + commission.upgradePlan.slice(1)} 
                      size="small" 
                    />
                  ) : 'N/A'}
                </TableCell>
                <TableCell>£{commission.commissionAmount.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip 
                    label={commission.commissionStatus} 
                    color={getStatusColor(commission.commissionStatus)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {commission.commissionEarnedAt 
                    ? new Date(commission.commissionEarnedAt).toLocaleDateString()
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  {commission.commissionPaidAt 
                    ? new Date(commission.commissionPaidAt).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {commission.commissionStatus === 'pending' && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setSelectedCommission(commission);
                        setApproveDialog(true);
                      }}
                    >
                      <CheckIcon />
                    </IconButton>
                  )}
                  {commission.commissionStatus === 'approved' && (
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => {
                        setSelectedCommission(commission);
                        setPayDialog(true);
                      }}
                    >
                      <PaymentIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            sx={{ mr: 2 }}
          >
            Previous
          </Button>
          <Typography sx={{ alignSelf: 'center', mx: 2 }}>
            Page {page} of {pagination.pages}
          </Typography>
          <Button
            disabled={page === pagination.pages}
            onClick={() => setPage(page + 1)}
            sx={{ ml: 2 }}
          >
            Next
          </Button>
        </Box>
      )}

      <Dialog open={approveDialog} onClose={() => setApproveDialog(false)}>
        <DialogTitle>Approve Commission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve this commission of £{selectedCommission?.commissionAmount.toFixed(2)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="primary">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={payDialog} onClose={() => setPayDialog(false)}>
        <DialogTitle>Mark Commission as Paid</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark this commission of £{selectedCommission?.commissionAmount.toFixed(2)} as paid?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(false)}>Cancel</Button>
          <Button onClick={handleMarkPaid} variant="contained" color="success">
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Commissions;

